/** S3 bucket hostname that needs proxying */
const S3_HOST = 'cms-devstage.s3.eu-central-1.amazonaws.com';

/**
 * Rewrite S3 URL to go through the server-side download proxy.
 *
 * S3 doesn't allow cross-origin fetch from the browser (CORS).
 * Instead of fetching S3 directly, we route through our own API:
 *   /api/download?url=<encoded S3 URL>
 *
 * The API route (shared/app/(api)/download.ts) fetches S3 server-side
 * and streams the response back with Content-Disposition: attachment.
 *
 * Each site re-exports the handler:
 *   app/api/download/route.ts → export { GET } from '@/shared/app/(api)/download'
 */
const proxyUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === S3_HOST) {
      return `/api/download?url=${encodeURIComponent(url)}`;
    }
  } catch {
    // not a valid URL
  }
  return url;
};

/**
 * Download a file by fetching as blob and triggering a save dialog.
 *
 * Flow:
 *   1. S3 URLs are rewritten to /api/download?url=... (same-origin, no CORS)
 *   2. API route fetches S3 server-side, responds with attachment headers
 *   3. Browser receives blob, creates object URL, triggers save dialog
 *   4. Falls back to window.open if anything fails
 *
 * @see shared/app/(api)/download.ts — server-side download proxy handler
 */
export const downloadFile = (url: string, filename: string): void => {
  fetch(proxyUrl(url))
    .then((res) => {
      if (!res.ok) throw new Error('fetch failed');
      return res.arrayBuffer();
    })
    .then((buffer) => {
      const blobUrl = window.URL.createObjectURL(new Blob([buffer]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    })
    .catch(() => {
      // @max, if download fails — ensure app/api/download/route.ts re-exports shared handler
      console.warn('[downloadFile] Fetch failed, falling back to window.open');
      window.open(url, '_blank', 'noopener,noreferrer');
    });
};
